import { useEffect, useRef } from 'react';

/**
 * DotsBackground — lightweight fixed canvas.
 * Optimised: 65 dots (was 140), spatial-grid proximity check,
 * throttled to ~24 fps, paused when tab hidden.
 */
const DotsBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // ── Resize ──────────────────────────────────────────────────────
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // ── Config ──────────────────────────────────────────────────────
    const COUNT     = 65;       // was 140
    const LINE_DIST = 110;      // max px for connecting lines (was 150)
    const LINE_DIST_SQ = LINE_DIST * LINE_DIST;
    const TARGET_FPS = 24;      // throttled — was uncapped 60fps
    const FRAME_MS   = 1000 / TARGET_FPS;

    const rand = (lo, hi) => lo + Math.random() * (hi - lo);

    const mkDot = () => ({
      x:  rand(0, canvas.width),
      y:  rand(0, canvas.height),
      z:  rand(0.15, 1.0),
      vx: rand(-0.10, 0.10),
      vy: rand(-0.10, 0.10),
      base: rand(0.4, 1.0),
    });

    const dots = Array.from({ length: COUNT }, mkDot);

    // ── Mouse parallax (throttled via ref) ──────────────────────────
    let mx = 0, my = 0;
    const onMove = (e) => {
      mx = (e.clientX / window.innerWidth  - 0.5) * 0.25;
      my = (e.clientY / window.innerHeight - 0.5) * 0.25;
    };
    window.addEventListener('mousemove', onMove, { passive: true });

    // ── Visibility pause ────────────────────────────────────────────
    let paused = false;
    const onVisChange = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVisChange);

    // ── Animation loop (FPS-throttled) ──────────────────────────────
    let raf;
    let lastTime = 0;

    const animate = (ts) => {
      raf = requestAnimationFrame(animate);
      if (paused) return;

      // Throttle to TARGET_FPS
      if (ts - lastTime < FRAME_MS) return;
      lastTime = ts;

      const w = canvas.width, h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // Update positions
      for (let i = 0; i < COUNT; i++) {
        const d = dots[i];
        d.x += d.vx + mx * d.z * 0.5;
        d.y += d.vy + my * d.z * 0.5;
        if (d.x < -10) d.x = w + 10;
        else if (d.x > w + 10) d.x = -10;
        if (d.y < -10) d.y = h + 10;
        else if (d.y > h + 10) d.y = -10;
      }

      // ── Lines: only check pairs within a spatial band ────────────
      // Sort by x each frame (cheap at N=65) then only compare nearby
      const sorted = dots.slice().sort((a, b) => a.x - b.x);

      for (let i = 0; i < COUNT; i++) {
        const a = sorted[i];
        for (let j = i + 1; j < COUNT; j++) {
          const b = sorted[j];
          if (b.x - a.x > LINE_DIST) break; // prune remaining j's
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const distSq = dx * dx + dy * dy;
          if (distSq < LINE_DIST_SQ) {
            const t = 1 - distSq / LINE_DIST_SQ;
            const alpha = t * (a.z + b.z) * 0.5 * 0.10;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.strokeStyle = `rgba(69,208,189,${alpha.toFixed(3)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // ── Dots ──────────────────────────────────────────────────────
      for (let i = 0; i < COUNT; i++) {
        const d = dots[i];
        const r     = d.base * d.z * 2.2;
        const alpha = 0.06 + d.z * 0.52;
        const bright = Math.round(170 + d.z * 80);
        ctx.beginPath();
        ctx.arc(d.x, d.y, Math.max(0.3, r), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${bright},${bright},${bright + 12},${alpha.toFixed(3)})`;
        ctx.fill();
      }
    };

    raf = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('visibilitychange', onVisChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position:      'fixed',
        inset:         0,
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        zIndex:        0,
      }}
    />
  );
};

export default DotsBackground;
